/** Layer groups: the operator's own named sets of layers, one tap to turn on
 * or off (2026-10-02, "maybe way to turn certain categories on and off too?
 * multiple layer groups? … it need to be easier to use").
 *
 * A group is a list of MEMBERS — entity category keys ("control", "energy")
 * and overlays as `overlay:<id>` ("overlay:pipelines", "overlay:firms") — so
 * "Energy" can be the energy places AND the pipelines, which live in two
 * different halves of the Layers panel. Groups are not exclusive views: two
 * can be on at once, and tapping one never touches a layer outside it.
 *
 * A tap turns the whole group ON unless it already is, in which case it
 * turns it OFF — so a half-on group goes on first, never straight off.
 *
 * Kept per browser, like every other layer preference in the panel (the
 * category switches, the type filter, the damage window). This module owns
 * the storage key and every rule that does not need the map.
 */

export interface LayerGroup {
  id: string;
  name: string;
  members: string[];
}

export type GroupState = 'on' | 'partial' | 'off';

export const LAYER_GROUPS_KEY = 'cockpit-layer-groups';
export const OVERLAY_PREFIX = 'overlay:';
export const GROUP_NAME_MAX = 40;
export const GROUPS_MAX = 24;

function store(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

/** The stored groups, cleaned: a corrupt key, a member listed twice or a
 *  group with no name or no members reads as absent, never as a crash. */
export function loadGroups(): LayerGroup[] {
  let raw: unknown;
  try {
    raw = JSON.parse(store()?.getItem(LAYER_GROUPS_KEY) ?? '[]');
  } catch {
    return [];
  }
  return normalizeGroups(raw);
}

export function normalizeGroups(raw: unknown): LayerGroup[] {
  if (!Array.isArray(raw)) return [];
  const out: LayerGroup[] = [];
  const ids = new Set<string>();
  for (const g of raw) {
    if (!g || typeof g !== 'object') continue;
    const { id, name, members } = g as Record<string, unknown>;
    if (typeof id !== 'string' || !id || ids.has(id)) continue;
    const clean = cleanName(name);
    if (!clean || !Array.isArray(members)) continue;
    const ms = [...new Set(members.filter((m): m is string => typeof m === 'string' && !!m))];
    if (!ms.length) continue;
    ids.add(id);
    out.push({ id, name: clean, members: ms });
    if (out.length >= GROUPS_MAX) break;
  }
  return out;
}

export function saveGroups(groups: LayerGroup[]): void {
  try {
    store()?.setItem(LAYER_GROUPS_KEY, JSON.stringify(groups));
  } catch {
    /* a private window or a full quota: the groups live for this session */
  }
}

export function cleanName(name: unknown): string {
  return typeof name === 'string'
    ? name.replace(/\s+/g, ' ').trim().slice(0, GROUP_NAME_MAX) : '';
}

/** Members this map can actually switch — a category retired since the
 *  group was made (or an overlay a guest has no row for) is skipped rather
 *  than leaving the group forever "half on". */
export function liveMembers(g: LayerGroup, known: (member: string) => boolean): string[] {
  return g.members.filter(known);
}

export function groupState(members: string[], isOn: (member: string) => boolean): GroupState {
  if (!members.length) return 'off';
  const on = members.filter(isOn).length;
  return on === members.length ? 'on' : on === 0 ? 'off' : 'partial';
}

/** What a tap sets every member to: off only when the group is fully on. */
export const tapTurnsOn = (state: GroupState): boolean => state !== 'on';

export function newGroupId(): string {
  return `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}
